/**
 * Rubrique Messages (décision du propriétaire du 25/10/2026) : formes des réponses
 * de l'API, clés de cache et lectures partagées par les écrans, la navigation
 * (pastille du nombre de non-lus) et l'accueil (annonces à lire).
 *
 * Pas de temps réel : chaque lecture se refait à l'ouverture de l'écran, au retour
 * au premier plan (React Query) et après une synchronisation (`useRefreshOnSync`).
 */
import { useEffect } from 'react';
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import type { ConversationKind, ReferenceKind } from '@myequip/shared';
import { api, ApiError } from '../../core/api.js';
import { t } from '../../core/i18n.js';
import { onSyncState } from '../../offline/sync.js';

export const MESSAGES_PATH = '/messages';
export const ANNOUNCEMENTS_PATH = '/messages?onglet=annonces';
export const NEW_CONVERSATION_PATH = '/messages/nouveau';
export const NEW_ANNOUNCEMENT_PATH = '/messages/annonces/nouvelle';
/** Article d'aide « Messages » (manuel du technicien, lu par tous les rôles internes). */
export const MESSAGES_HELP_PATH = '/aide/sujets/technicien/messages-conversations-et-annonces';

export const conversationPath = (id: string): string => `/messages/${encodeURIComponent(id)}`;
export const announcementPath = (id: string): string => `/messages/annonces/${encodeURIComponent(id)}`;

export const KEYS = {
  conversations: ['conversations'] as const,
  conversationsUnread: ['conversations', 'unread'] as const,
  conversation: (id: string) => ['conversation', id] as const,
  messages: (id: string) => ['conversation', id, 'messages'] as const,
  announcements: ['announcements'] as const,
  announcementsUnread: ['announcements', 'unread'] as const,
  announcement: (id: string) => ['announcement', id] as const,
  readers: (id: string) => ['announcement', id, 'readers'] as const,
  people: ['messaging-people'] as const,
};

export interface ConversationMember {
  id: string;
  name: string | null;
  avatarFileId: string | null;
  isAdmin: boolean;
}

export interface ConversationListItem {
  id: string;
  kind: ConversationKind;
  title: string | null;
  members: ConversationMember[];
  activityAt: string;
  lastMessage: {
    seq: number; authorId: string; authorName: string | null; preview: string;
    withdrawn: boolean; files: number; refs: number; createdAt: string;
  } | null;
  unread: boolean;
  muted: boolean;
  isAdmin: boolean;
}

export interface ConversationDetail {
  id: string;
  kind: ConversationKind;
  title: string | null;
  createdBy: string;
  createdAt: string;
  lastMessageSeq: number;
  members: ConversationMember[];
  me: { isAdmin: boolean; muted: boolean; lastReadSeq: number };
}

export type ResolvedReference =
  | { kind: ReferenceKind; id: string; label: string; to: string; accessible: true }
  | { kind: ReferenceKind; accessible: false };

export interface MessageFile {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  scanStatus: string;
}

export interface Message {
  id: string;
  seq: number;
  authorId: string;
  authorName: string | null;
  body: string;
  refs: ResolvedReference[];
  files: MessageFile[];
  withdrawnAt: string | null;
  createdAt: string;
}

export interface AnnouncementStats { recipients: number; seen: number; acknowledged: number }

export interface AnnouncementListItem {
  id: string;
  title: string;
  preview: string;
  authorId: string;
  authorName: string | null;
  publishedAt: string;
  urgent: boolean;
  requiresAck: boolean;
  archivedAt: string | null;
  replacesId: string | null;
  files: number;
  mine: { seenAt: string | null; acknowledgedAt: string | null } | null;
  pending: boolean;
  stats: AnnouncementStats | null;
}

export interface AnnouncementDetail extends Omit<AnnouncementListItem, 'preview' | 'files' | 'pending'> {
  body: string;
  audience: { kind: 'tous' } | { kind: 'roles' | 'membres'; ids: string[] } | null;
  files: MessageFile[];
  lastRemindedAt: string | null;
  nextReminderAt: string | null;
  canManage: boolean;
  replacedById: string | null;
}

export interface AnnouncementReaders {
  requiresAck: boolean;
  recipients: number;
  seen: number;
  acknowledged: number;
  lastRemindedAt: string | null;
  nextReminderAt: string | null;
  readers: { id: string; name: string | null; seenAt: string | null; acknowledgedAt: string | null }[];
}

export interface MessagingPerson {
  id: string;
  name: string | null;
  avatarFileId: string | null;
  roleId: string | null;
  roleName: string | null;
}

export interface MessagingDirectory {
  people: MessagingPerson[];
  roles?: { id: string; name: string; members: number }[];
}

/** Nom affiché d'une personne (compte supprimé : « Ancien membre »). */
export function personName(name: string | null | undefined): string {
  return name?.trim() || t('messages.ancienMembre');
}

/** Nom d'une conversation vu par ce compte : le titre du groupe, sinon les autres membres. */
export function conversationTitle(
  conversation: Pick<ConversationListItem, 'kind' | 'title' | 'members'>, meId: string | undefined,
): string {
  if (conversation.title) return conversation.title;
  const others = conversation.members.filter((member) => member.id !== meId);
  if (conversation.kind === 'direct') return personName(others[0]?.name);
  if (others.length === 0) return t('messages.groupeSansNom', { count: conversation.members.length });
  const names = others.map((member) => personName(member.name).split(/\s+/)[0]);
  return names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3}` : names.join(', ');
}

/** Ligne d'aperçu du dernier message, jamais plus de ce que l'API donne (100 caractères). */
export function lastMessageLine(item: ConversationListItem, meId: string | undefined): string {
  const last = item.lastMessage;
  if (!last) return t('messages.aucunMessage');
  const who = last.authorId === meId ? t('messages.vous') : personName(last.authorName).split(/\s+/)[0];
  const what = last.withdrawn ? t('messages.conversation.retire')
    : last.preview.trim() ? last.preview.replace(/\s+/g, ' ').trim()
      : last.files > 0 ? t(last.files > 1 ? 'messages.piecesJointes' : 'messages.pieceJointe', { n: last.files })
        : t('messages.ficheJointe');
  return item.kind === 'groupe' || last.authorId === meId ? `${who} : ${what}` : what;
}

/** Message lisible d'un échec, « hors ligne » compris. */
export function errorText(error: unknown): string {
  if (error instanceof ApiError && error.code === 'network_error') return t('messages.conversation.reseauRequis');
  return error instanceof Error ? error.message : String(error);
}

export interface UnreadCounts {
  conversations: number;
  announcements: number;
  total: number;
}

/**
 * Non-lus de la rubrique (conversations hors sourdine, annonces à lire ou à confirmer),
 * pour la pastille de la navigation et les onglets. Lus seulement avec le droit.
 */
export function useMessagesUnread(enabled: boolean): UnreadCounts | undefined {
  const conversations = useQuery({
    queryKey: KEYS.conversationsUnread,
    queryFn: () => api<{ count: number }>('/conversations/unread-count'),
    enabled,
    staleTime: 30_000,
  });
  const announcements = useQuery({
    queryKey: KEYS.announcementsUnread,
    queryFn: () => api<{ count: number }>('/announcements/unread-count'),
    enabled,
    staleTime: 30_000,
  });
  useRefreshOnSync([KEYS.conversationsUnread, KEYS.announcementsUnread], enabled);
  if (!enabled || (!conversations.data && !announcements.data)) return undefined;
  const c = conversations.data?.count ?? 0;
  const a = announcements.data?.count ?? 0;
  return { conversations: c, announcements: a, total: c + a };
}

/**
 * Annonces à lire ou à confirmer, pour la file « À traiter » de l'accueil : la liste
 * épinglée de la première page (`pinned`). Relue avec les autres annonces.
 */
export function useHomeAnnouncements(enabled: boolean) {
  return useQuery({
    queryKey: [...KEYS.announcements, 'accueil'],
    queryFn: () => api<{ pinned: AnnouncementListItem[] }>('/announcements?limit=1'),
    enabled,
    staleTime: 30_000,
  });
}

/** Annuaire de la messagerie : qui l'écran peut proposer (rôles pour qui publie). */
export function useMessagingPeople(enabled = true) {
  return useQuery({
    queryKey: KEYS.people,
    queryFn: () => api<MessagingDirectory>('/messaging/people'),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * Une synchronisation terminée (retour du réseau, envoi de la file) relit ces
 * données : un message parti de la file apparaît, un non-lu arrivé entre-temps aussi.
 */
export function useRefreshOnSync(keys: readonly QueryKey[], enabled = true): void {
  const queryClient = useQueryClient();
  const signature = JSON.stringify(keys);
  useEffect(() => {
    if (!enabled) return undefined;
    let last: string | undefined;
    let pending: number | undefined;
    return onSyncState((state) => {
      const marker = `${state.lastSyncAt ?? ''}|${state.pendingOps}|${state.refused}`;
      if (last === undefined) {
        last = marker;
        return;
      }
      if (marker === last) return;
      last = marker;
      window.clearTimeout(pending);
      pending = window.setTimeout(() => {
        for (const queryKey of JSON.parse(signature) as QueryKey[]) void queryClient.invalidateQueries({ queryKey });
      }, 300);
    });
  }, [signature, enabled, queryClient]);
}

/** Après une lecture, un envoi ou un accusé : la liste et les pastilles se relisent. */
export function invalidateCounters(queryClient: ReturnType<typeof useQueryClient>): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: KEYS.conversations }),
    queryClient.invalidateQueries({ queryKey: KEYS.announcements }),
  ]);
}
